import {
  AcquiredCertificationsArray,
  CiiuArray,
  Company,
  CreateCompanyDto,
  DriverQuestion,
  VehicleQuestion,
} from "@/interfaces/Company";
import { MisionalitySizeCriteria, Mission } from "@/interfaces/Dedication";
import axiosBaseQuery from "@/utils/axiosBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";
import { corporateGroupService } from "./corporateGroupService";
import { userService } from "./userService";

/**
 * IMPORTANTE:
 * Debido a una limitación actual, no se pueden exportar los hooks individualmente sin causar problemas de tipos.
 * El siguiente código es un intento de exportar el hook useLoginMutation, pero resulta en el siguiente error de TypeScript:
 * "El tipo inferido de 'useLoginMutation' no se puede nombrar sin una referencia a '../../../node_modules/@reduxjs/toolkit/dist/query/react/buildHooks'. Es probable que esto no sea portátil. Es necesaria una anotación de tipo.ts(2742)"
 * en su lugar llamar directamente a authService y acceder al hook
 * @example
   companyService.findAll(...params)
 */

// const { useLoginMutation } = companyService;
// export { useLoginMutation }; // Esta exportación causa un error de TypeScript
export const companyService = createApi({
  reducerPath: "companyApi",
  baseQuery: axiosBaseQuery,
  tagTypes: ["GetCompamies"],
  endpoints: (builder) => ({
    findAll: builder.query<
      Company[],
      { arlId?: number | null; consultantId?: number | null }
    >({
      query: ({ arlId, consultantId }) => ({
        url: `/companies/`,
        method: "GET",
        params: {
          arlId: arlId !== undefined ? arlId : null,
          consultor: consultantId !== undefined ? consultantId : null,
        },
      }),
      providesTags: ["GetCompamies"],
    }),
    findAllDetail: builder.query<
      { companies: Company[] },
      { arlId?: number | null; consultantId?: number | null; dataType: string }
    >({
      query: ({ arlId, consultantId, dataType }) => ({
        url: `/${dataType}/company/all-detail`,
        method: "GET",
        params: {
          arlId: arlId !== undefined ? arlId : null,
          consultor: consultantId !== undefined ? consultantId : null,
        },
      }),
      providesTags: ["GetCompamies"],
    }),
    findById: builder.query<Company, { id: number }>({
      query: ({ id }) => ({
        url: `/audit/company/${id}`,
        method: "GET",
      }),
    }),
    updateCompany: builder.mutation<Company, Partial<CreateCompanyDto>>({
      query: ({ id, ...rest }) => ({
        url: `/companies/${id}`,
        method: "PATCH",
        data: {
          ...rest,
        },
      }),
      invalidatesTags: ["GetCompamies"],
    }),
    deleteCompany: builder.mutation<{ id: number } | null, { id: number }>({
      query: ({ id }) => ({
        url: `/companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["GetCompamies"],
    }),
    save: builder.mutation<
      {
        company: Company;
        diagnosis_id: number | null;
      },
      CreateCompanyDto
    >({
      query: (companyData) => ({
        url: `/companies/`,
        method: "POST",
        data: {
          ...companyData,
        },
      }),
      invalidatesTags: ["GetCompamies"],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        // Invalida el tag 'User' en el servicio 'userApi'
        dispatch(
          corporateGroupService.util.invalidateTags(["CompaniesNotIncorpored"])
        );
        dispatch(userService.util.invalidateTags(["FindUserById"]));
      },
    }),
    // save: builder.mutation<Company, CompanyDTO>({
    //   query: (companyData) => ({
    //     url: `/companies/`,
    //     method: "POST",
    //     data: {
    //       ...companyData,
    //     },
    //   }),
    //   invalidatesTags: ["GetCompamies"],
    //   onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
    //     await queryFulfilled;
    //     // Invalida el tag 'User' en el servicio 'userApi'
    //     dispatch(
    //       corporateGroupService.util.invalidateTags(["CompaniesNotIncorpored"])
    //     );
    //     dispatch(userService.util.invalidateTags(["FindUserById"]));
    //   },
    // }),
    findAllDedications: builder.query<
        { 
          missions: Mission[]
        }, 
        void
      >({
      query: () => ({
        url: `/missions`,
        method: "GET",
      }),
    }),
    findcompanySizeByDedicactionId: builder.query<
      { 
        missionality_size_criteria : MisionalitySizeCriteria[] 
      },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/misionality-size-criteria?mission=${id}`,
        method: "GET",
      }),
    }),
    findAllVehicleQuestions: builder.query<VehicleQuestion[], void>({
      query: () => ({
        url: `/audit-questions`,
        method: "GET",
      }),
      transformResponse: (response: { driver_questions: VehicleQuestion[] }) => 
        response.driver_questions,
    }),
    findAllDriverQuestions: builder.query<DriverQuestion[], void>({
      query: () => ({
        url: `/audit-questions`,
        method: "GET",
      }),
      transformResponse: (response: { vehicle_questions: DriverQuestion[] }) => 
        response.vehicle_questions,
    }),
    findCiiuByCode: builder.query<CiiuArray, { codeCiiu?: string }>({
      query: ({ codeCiiu }) => ({
        // url: `/companies/findCiiuByCode`,
        url: `/ciius`,
        method: "GET",
        params: {
          ciiu_code: codeCiiu,
        },
      }),
    }),
    findAcquiredCertificationsByCode: builder.query<
      AcquiredCertificationsArray,
      { certification_code?: string }
    >({
      query: ({ certification_code }) => ({
        url: `/acquired-certifications/`,
        method: "GET",
        params: {
          certification_code: certification_code,
        },
      }),
    }),
    auditByCompany: builder.query<any, void>({
      query: () => ({
        url: `/audit/count/?action=by_company`,
        method: "GET",
      }),
    }),
    findCompanyByNit: builder.query<Company, { nit: string }>({
      query: ({ nit }) => ({
        url: `/companies/nit/${nit}`,
        method: "GET",
      }),
      transformResponse: (response: { company?: Company } | Company) =>
        "company" in response && response.company
          ? response.company
          : (response as Company),
    }),
  }),
});
